/**
 * The season in scope, and why this chapter has one at all.
 *
 * `GET /postseason/suppliers` and `GET /postseason/recovery-runs` now take a `season`,
 * and that is a G16 fix rather than a convenience: both reads used to run over the whole
 * estate, so a scorecard screen asked the store for every supplier of every season it has
 * ever held and then rendered the first page of it. The bound is the point, so this
 * module exists to make it impossible for a screen to reach those operations without one.
 *
 * **There is no default season.** A default would re-create the unbounded read with an
 * extra step — a screen that silently reads `SS26` because nothing was chosen has still
 * chosen a perimeter on the planner's behalf, and it would be wrong every season but one.
 * `readSeasonScope()` returns `null` when nothing is in scope and the screens render a
 * designed "choose a season" state instead of a table (G2 §2.6).
 *
 * **The season lives in the URL.** G2.2: "THE URL IS THE STATE. Any screen, filter set,
 * drill path and scenario is a shareable, bookmarkable link." A post-season pack mailed
 * to a merchandise director has to open on the season it was written about, which a
 * component-local `useState` cannot do. `src/app/routes/router.tsx` holds the same rule
 * and the same two lines, and this module does NOT import it: `no-restricted-imports`
 * forbids `@/app/*` because the shell composes features and not the reverse (G11), so the
 * two-line read of `window.location.search` is repeated here rather than the dependency
 * direction being inverted. Nothing else is duplicated.
 *
 * **The seasons come from `GET /postseason/seasons`** (`useSeasonDirectory` in `panels.tsx`),
 * the platform's season directory — every season with a recovery run or a supplier scorecard.
 * This module used to record the GAP that there was no such read, and the picker derived its
 * options from the season-review mart instead; the platform closed the gap, and on 2026-09-25
 * the mart turned out never to be wired, so the derived picker hid every post-season screen.
 */

import { useCallback, useEffect, useState } from 'react';

/** The search parameter carrying the perimeter. One name, used by screens and exports. */
export const SEASON_PARAM = 'season';

/**
 * The season in scope, or `null`.
 *
 * Usable outside React — the export resolvers in `exports.ts` are plain async functions
 * with no hook context and they must export the perimeter the planner is looking at
 * (G2.4's fidelity rule), so they read the same source through the same function.
 */
export function readSeasonScope(): string | null {
  if (typeof window === 'undefined') return null;
  const value = new URLSearchParams(window.location.search).get(SEASON_PARAM);
  return value !== null && value.trim() !== '' ? value : null;
}

/**
 * Writes the season into the URL without pushing a history entry.
 *
 * `replaceState`, not `pushState`: choosing a perimeter is a refinement of the screen the
 * planner is on rather than a navigation, and pushing would make the browser back button
 * walk the picker instead of the drill path — which G2.2 reserves for the level path.
 */
function writeSeasonScope(season: string): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  params.set(SEASON_PARAM, season);
  const search = params.toString();
  window.history.replaceState(null, '', `${window.location.pathname}?${search}`);
}

export interface SeasonScope {
  /** `null` means nothing is in scope. It does NOT mean "all seasons". */
  readonly season: string | null;
  readonly setSeason: (season: string) => void;
}

/** The season in scope, kept in step with the URL and with browser back/forward. */
export function useSeasonScope(): SeasonScope {
  const [season, setSeasonState] = useState<string | null>(() => readSeasonScope());

  useEffect(() => {
    const onPopState = (): void => setSeasonState(readSeasonScope());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const setSeason = useCallback((next: string) => {
    writeSeasonScope(next);
    setSeasonState(next);
  }, []);

  return { season, setSeason };
}

/**
 * DERIVE — the seasons a planner may choose between, from the rows of a mart.
 *
 * Sorted descending, because a post-season review is read about the season that just
 * ended far more often than about any other, and an ascending list puts the oldest first.
 */
export function seasonsFrom(rows: readonly { readonly season: string }[]): readonly string[] {
  return [...new Set(rows.map((row) => row.season).filter((season) => season !== ''))]
    .sort()
    .reverse();
}
